import { useCallback, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { PickerProvider, type PickerApi } from '@/lib/forms/PickerContext';
import { Dialog } from '@/lib/ui/Dialog';
import { mediaApi } from './api';
import { keys } from '@/lib/query/keys';
import { MediaBrowser } from './MediaBrowser';
import { EntityPickerModal } from '@/features/content/EntityPickerModal';

/**
 * Hosts the media + entity picker modals and exposes imperative pick() resolvers to the
 * schema-form widgets via PickerProvider (plan §4.7). Decouples forms from the media module.
 */
export function MediaPickerHost({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  const [mediaOpen, setMediaOpen] = useState(false);
  const [entityOpen, setEntityOpen] = useState<{ collection: string } | null>(null);
  const mediaResolver = useRef<((id: string | null) => void) | null>(null);
  const entityResolver = useRef<((id: string | null) => void) | null>(null);

  const pickMedia = useCallback<PickerApi['pickMedia']>(() => {
    setMediaOpen(true);
    return new Promise<string | null>((resolve) => {
      mediaResolver.current = resolve;
    });
  }, []);

  const pickEntity = useCallback<PickerApi['pickEntity']>((collection) => {
    setEntityOpen({ collection });
    return new Promise<string | null>((resolve) => {
      entityResolver.current = resolve;
    });
  }, []);

  const api: PickerApi = {
    pickMedia,
    pickEntity,
    MediaPreview: ({ id }) => <MediaPreview id={id} />,
  };

  const closeMedia = (id: string | null) => {
    mediaResolver.current?.(id);
    mediaResolver.current = null;
    setMediaOpen(false);
  };
  const closeEntity = (id: string | null) => {
    entityResolver.current?.(id);
    entityResolver.current = null;
    setEntityOpen(null);
  };

  return (
    <PickerProvider value={api}>
      {children}
      <Dialog
        open={mediaOpen}
        onOpenChange={(o) => !o && closeMedia(null)}
        title={t('media.chooseMedia')}
        size="xl"
      >
        <MediaBrowser canUpload onSelect={(asset) => closeMedia(asset.id)} />
      </Dialog>
      {entityOpen && (
        <EntityPickerModal
          collection={entityOpen.collection}
          open
          onClose={(id) => closeEntity(id)}
        />
      )}
    </PickerProvider>
  );
}

function MediaPreview({ id }: { id: string | null }) {
  const query = useQuery({
    queryKey: keys.media.detail(id ?? 'none'),
    queryFn: () => mediaApi.get(id!),
    enabled: !!id,
    staleTime: 60_000,
  });
  if (!id) return <div className="h-[44px] w-[44px] rounded-sm bg-canvas-deep-sand" aria-hidden />;
  const asset = query.data;
  if (asset?.kind === 'image' && !asset.is_restricted) {
    return (
      <img
        src={asset.derivatives?.thumb?.url ?? asset.url}
        alt={asset.alt?.en ?? ''}
        className="h-[44px] w-[44px] rounded-sm object-cover"
      />
    );
  }
  return (
    <div className="flex h-[44px] w-[44px] items-center justify-center rounded-sm bg-canvas-deep-sand text-ink-mute">
      {asset ? '📄' : '…'}
    </div>
  );
}
